import React, { createContext, useCallback, useEffect, useMemo, useReducer } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import ROUTES from 'src/constants/routes'
import { API } from 'src/services/config'
import { useTokenRefresh } from './useTokenRefresh.hook'
import { STORAGE_KEYS, LOGIN_STATUS } from './constants'
import type { AuthContextInterface, AuthProviderProps } from './types'
import { useRegisterUser } from 'src/hooks/api/user/mutations/useRegisterUsername'
import { authReducer, initialAuthState } from './reducers/auth.reducer'
import { useLogin } from './hooks/useLogin'
import { useLogout } from './hooks/useLogout'
import { useRegister } from './hooks/useRegister'
import { useConfirmation } from './hooks/useConfirmation'
import { useAuthInitialize } from './hooks/useAuthInitialize'
import { needsPhoneConfirmation } from './utils/confirmation.utils'
import { storageService } from './services/storage.service'
import { isTokenValid as isTokenStillValid } from './services/token.service'
import { UserController } from 'src/controllers/user.controller'
import { IUser } from 'src/@types/User'

const userController = new UserController()

export const AuthContext = createContext<AuthContextInterface | undefined>(undefined)

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(authReducer, initialAuthState)

  const { mutateAsync: registerUser, isPending: isRegistering } = useRegisterUser()

  const handleTokenExpired = useCallback(() => {
    storageService.clearSession()

    if (API.defaults.headers) {
      delete API.defaults.headers['Authorization']
    }

    dispatch({ type: 'SESSION_EXPIRED' })
    queryClient.clear()
    navigate(ROUTES.AUTH.LOGIN)
  }, [navigate, queryClient])

  const handleTokenRefreshed = useCallback(() => {
    // O estado de token expirado permanece verdadeiro enquanto a sessão é válida.
  }, [])

  // Hook de renovação de token
  const { scheduleTokenRefresh, cancelTokenRefresh, isTokenValid } = useTokenRefresh({
    onTokenExpired: handleTokenExpired,
    onTokenRefreshed: handleTokenRefreshed
  })

  const login = useLogin({ dispatch, navigate, scheduleTokenRefresh })
  const logout = useLogout({ dispatch, navigate, queryClient, cancelTokenRefresh })
  const register = useRegister({
    registerUser,
    login,
    navigate,
    setStatusLogin: (status) => dispatch({ type: 'SET_STATUS_LOGIN', payload: status })
  })

  const switchFarm = useCallback(
    (farmId: string) => {
      dispatch({ type: 'SET_USER_FARM', payload: farmId })
      localStorage.setItem(STORAGE_KEYS.ID_FAZENDA, farmId)

      const currentUser = storageService.getUser()
      if (currentUser) {
        localStorage.setItem('userData', JSON.stringify({ ...currentUser, idFazenda: farmId }))
      }

      queryClient.clear()
    },
    [queryClient]
  )

  const refreshUserData = useCallback(async () => {
    const farmId = state.user?.idFazenda
    const username = state.user?.username

    if (!farmId || !username) return

    try {
      const users = await userController.getUserListFromFarm(farmId)
      const freshUser = users.find((u) => u.username === username)

      if (freshUser) {
        const userData = Object.fromEntries(
          Object.entries(freshUser).filter(([key]) => key !== 'senha')
        ) as Partial<IUser>

        dispatch({ type: 'UPDATE_USER', payload: userData })

        const currentUser = storageService.getUser()
        if (currentUser) {
          localStorage.setItem('userData', JSON.stringify({ ...currentUser, ...userData }))
        }
      }
    } catch {
      // Falha silenciosa — dados stale do localStorage continuam funcionando
    }
  }, [state.user?.idFazenda, state.user?.username])

  useAuthInitialize({ dispatch, scheduleTokenRefresh })

  useEffect(() => {
    if (state.user?.idFazenda && state.user?.username && state.token) {
      void refreshUserData()
    }
  }, [state.token])

  const { validationPhone, validationEmail, resendPhoneCode, resendEmailCode } = useConfirmation({
    dispatch,
    username: state.user?.username
  })

  /**
   * Limpa statusLogin após erro
   */
  useEffect(() => {
    if (state.statusLogin === LOGIN_STATUS.ERROR) {
      const timer = setTimeout(() => {
        dispatch({ type: 'SET_STATUS_LOGIN', payload: '' })
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [state.statusLogin])

  const name = `${state.user?.nome ?? ''} ${state.user?.sobrenome ?? ''}`.trim()
  const phoneConfimed = state.user?.telefoneConfirmado ?? localStorage.getItem(STORAGE_KEYS.PHONE_IS_CONFIRMED) ?? ''
  const emailConfimed = state.user?.emailConfirmado ?? localStorage.getItem(STORAGE_KEYS.EMAIL_IS_CONFIRMED) ?? ''

  const authenticated = !!(state.user && state.token && state.tokenExpired)

  const isAuthenticated = useCallback(() => {
    return !!(storageService.getToken() && storageService.getUser() && isTokenStillValid())
  }, [])

  const needsUserConfirmation = useCallback(() => {
    return needsPhoneConfirmation(phoneConfimed)
  }, [phoneConfimed])

  const contextValue: AuthContextInterface = useMemo(
    () => ({
      user: state.user ?? undefined,
      authenticated,
      username: state.user?.username,
      loading: state.loading || isRegistering,
      login,
      logout,
      switchFarm,
      refreshUserData,
      userFarm: state.user?.idFazenda,
      farmConfig: state.farmConfig,
      name,
      profile: state.user?.perfil,
      tokenExpired: state.tokenExpired,
      register,
      phoneRegistered: state.user?.telefone,
      validationPhone,
      phoneConfimed,
      emailRegistered: state.user?.username,
      validationEmail,
      emailConfimed,
      resendPhoneCode,
      resendEmailCode,
      statusLogin: state.statusLogin,
      isAuthenticated,
      isTokenValid,
      needsConfirmation: needsUserConfirmation
    }),
    [
      authenticated,
      emailConfimed,
      isRegistering,
      isTokenValid,
      isAuthenticated,
      login,
      logout,
      switchFarm,
      refreshUserData,
      name,
      needsUserConfirmation,
      phoneConfimed,
      register,
      resendEmailCode,
      resendPhoneCode,
      state.farmConfig,
      state.loading,
      state.statusLogin,
      state.tokenExpired,
      state.user,
      validationEmail,
      validationPhone
    ]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
